import { createFileRoute } from "@tanstack/react-router";
import { BackToDashboard } from "@/components/BackToDashboard";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { listShiftTemplates, upsertShiftTemplate, deleteShiftTemplate, type ShiftTemplateRow } from "@/lib/shift-templates.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/venue/templates")({
  head: () => ({ meta: [
    { title: "Shift templates — The Hospitality Reserve" },
    { name: "description", content: "Save shift templates and repost in two clicks." },
  ] }),
  component: TemplatesPage,
});

type T = ShiftTemplateRow;

function TemplatesPage() {
  const fetchList = useServerFn(listShiftTemplates);
  const upsert = useServerFn(upsertShiftTemplate);
  const del = useServerFn(deleteShiftTemplate);
  const [items, setItems] = useState<T[]>([]);
  const [editing, setEditing] = useState<Partial<T> | null>(null);
  const [busy, setBusy] = useState(false);

  async function reload() {
    const r = await fetchList();
    setItems(r.templates ?? []);
  }
  useEffect(() => { void reload(); /* eslint-disable-next-line */ }, []);

  async function save() {
    if (!editing) return;
    setBusy(true);
    const r = await upsert({ data: {
      id: editing.id,
      name: editing.name ?? "",
      role_type: editing.role_type ?? "bartender",
      hourly_rate: Number(editing.hourly_rate ?? 15),
      positions_needed: Number(editing.positions_needed ?? 1),
      duration_minutes: Number(editing.duration_minutes ?? 360),
      start_offset_minutes: null,
      notes: editing.notes ?? null,
      requires_dbs: !!editing.requires_dbs,
      requires_rtw: editing.requires_rtw ?? true,
      requires_mixologist: !!editing.requires_mixologist,
      postcode: editing.postcode ?? null,
    } });
    setBusy(false);
    if ("error" in r) { toast.error(r.error); return; }
    toast.success("Template saved");
    setEditing(null);
    void reload();
  }

  async function remove(id: string) {
    if (!confirm("Delete this template?")) return;
    await del({ data: { id } });
    void reload();
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      <BackToDashboard className="mb-4" />
      <header className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-2xl">Shift templates</h1>
          <p className="text-muted-foreground text-sm mt-1">Save recurring shift setups so you can repost in two clicks.</p>
        </div>
        <button onClick={() => setEditing({ name: "", role_type: "bartender", hourly_rate: 15, positions_needed: 1, duration_minutes: 360, requires_rtw: true })} className="bg-accent text-accent-foreground px-4 py-2 text-sm">
          New template
        </button>
      </header>

      {editing && (
        <section className="mt-6 border border-border p-4 space-y-3 bg-card">
          <h2 className="font-medium">{editing.id ? "Edit" : "New"} template</h2>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <label className="col-span-2">Name
              <input className="w-full mt-1 border border-border px-2 py-1.5 bg-background"
                value={editing.name ?? ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            </label>
            <label>Role
              <input className="w-full mt-1 border border-border px-2 py-1.5 bg-background"
                value={editing.role_type ?? ""} onChange={(e) => setEditing({ ...editing, role_type: e.target.value })} />
            </label>
            <label>Rate (£/hr)
              <input type="number" min={1} step="0.5" className="w-full mt-1 border border-border px-2 py-1.5 bg-background"
                value={editing.hourly_rate ?? 15} onChange={(e) => setEditing({ ...editing, hourly_rate: Number(e.target.value) })} />
            </label>
            <label>Positions
              <input type="number" min={1} max={50} className="w-full mt-1 border border-border px-2 py-1.5 bg-background"
                value={editing.positions_needed ?? 1} onChange={(e) => setEditing({ ...editing, positions_needed: Number(e.target.value) })} />
            </label>
            <label>Duration (minutes)
              <input type="number" min={60} step={30} className="w-full mt-1 border border-border px-2 py-1.5 bg-background"
                value={editing.duration_minutes ?? 360} onChange={(e) => setEditing({ ...editing, duration_minutes: Number(e.target.value) })} />
            </label>
            <label>Postcode (optional)
              <input className="w-full mt-1 border border-border px-2 py-1.5 bg-background"
                value={editing.postcode ?? ""} onChange={(e) => setEditing({ ...editing, postcode: e.target.value })} />
            </label>
            <label className="col-span-2">Notes
              <textarea className="w-full mt-1 border border-border px-2 py-1.5 bg-background min-h-[60px]"
                value={editing.notes ?? ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
            </label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.requires_dbs} onChange={(e) => setEditing({ ...editing, requires_dbs: e.target.checked })} />Requires DBS</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.requires_mixologist} onChange={(e) => setEditing({ ...editing, requires_mixologist: e.target.checked })} />Requires Mixologist</label>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-sm border border-border">Cancel</button>
            <button disabled={busy} onClick={save} className="px-3 py-1.5 text-sm bg-accent text-accent-foreground disabled:opacity-50">Save</button>
          </div>
        </section>
      )}

      <ul className="mt-6 divide-y divide-border border border-border">
        {items.length === 0 && <li className="p-4 text-sm text-muted-foreground">No templates yet.</li>}
        {items.map((t) => (
          <li key={t.id} className="p-4 flex justify-between gap-3">
            <div>
              <p className="font-medium">{t.name}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {t.role_type} · £{Number(t.hourly_rate).toFixed(2)}/hr · {t.positions_needed} pos · {Math.round(t.duration_minutes / 60)}h
                {t.requires_dbs ? " · DBS" : ""}{t.requires_mixologist ? " · Mixologist" : ""}
              </p>
            </div>
            <div className="flex gap-3 items-start text-xs">
              <button onClick={() => setEditing(t)} className="underline">Edit</button>
              <button onClick={() => remove(t.id)} className="text-destructive">Delete</button>
            </div>
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground mt-3">
        Tip: open the post-a-shift page from your dashboard and pre-fill from a template. (Pre-fill UI ships in Phase 2.)
      </p>
    </div>
  );
}
